/**
 * Template Builder - Basic Info Step
 * 
 * Collects basic template information:
 * - Title
 * - Destination
 * - Short description
 * - Long description (optional)
 * - Duration
 * - Difficulty
 * - Travel styles
 * - Tags
 * - Highlights
 * - Best months
 */

import React from 'react';
import { View, StyleSheet, ScrollView, Pressable } from 'react-native';
import { useTheme } from '@/theme';
import { InputField } from '@/components/ui/InputField';
import { SelectField } from '@/components/ui/SelectField';
import { NumberStepper } from '@/components/ui/NumberStepper';
import { Card } from '@/components/ui/Card';
import { ThemedText, Body, Caption } from '@/components/ui/Text';
import type {
  TemplateDifficulty,
  TravelStyle,
} from '@/types/tripTemplates';
import { normalizeTemplateTagInput } from '@/utils/templateTags';

type TemplateDraft = any; // Simplified for now

type TemplateBuilderBasicStepProps = {
  draft: Partial<TemplateDraft>;
  onUpdate: (updates: Partial<TemplateDraft>) => void;
  errors: Record<string, string>;
};

const DIFFICULTY_OPTIONS = [
  { value: 'easy', label: 'Easy' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'adventurous', label: 'Adventurous' },
];

const TRAVEL_STYLE_OPTIONS = [
  { value: 'cultural', label: 'Cultural' },
  { value: 'adventure', label: 'Adventure' },
  { value: 'relaxation', label: 'Relaxation' },
  { value: 'food', label: 'Food & Dining' },
  { value: 'urban', label: 'Urban' },
  { value: 'nature', label: 'Nature' },
  { value: 'beach', label: 'Beach' },
  { value: 'luxury', label: 'Luxury' },
  { value: 'budget', label: 'Budget' },
  { value: 'family', label: 'Family-Friendly' },
  { value: 'romantic', label: 'Romantic' },
  { value: 'solo', label: 'Solo Travel' },
  { value: 'road-trip', label: 'Road Trip' },
  { value: 'safari', label: 'Safari' },
];

const MONTH_OPTIONS = [
  { value: 'Jan', label: 'January' },
  { value: 'Feb', label: 'February' },
  { value: 'Mar', label: 'March' },
  { value: 'Apr', label: 'April' },
  { value: 'May', label: 'May' },
  { value: 'Jun', label: 'June' },
  { value: 'Jul', label: 'July' },
  { value: 'Aug', label: 'August' },
  { value: 'Sep', label: 'September' },
  { value: 'Oct', label: 'October' },
  { value: 'Nov', label: 'November' },
  { value: 'Dec', label: 'December' },
];

export const TemplateBuilderBasicStep: React.FC<TemplateBuilderBasicStepProps> = ({
  draft,
  onUpdate,
  errors,
}) => {
  const theme = useTheme();

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <Card style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Basic Information</ThemedText>
        <Caption color={theme.colors.textSecondary} style={styles.sectionDescription}>
          Provide the essential details about your trip template
        </Caption>

        <View style={styles.fields}>
          <InputField
            label="Template Title"
            placeholder="e.g., Seoul 7-Day Cultural Immersion"
            value={draft.title || ''}
            onChangeText={(text) => onUpdate({ title: text })}
            errorText={errors.title}
            helperText="A catchy title that describes your trip"
            style={styles.field}
          />

          <InputField
            label="Destination"
            placeholder="e.g., Seoul, South Korea"
            value={draft.destination || ''}
            onChangeText={(text) => onUpdate({ destination: text })}
            errorText={errors.destination}
            helperText="City and country"
            style={styles.field}
          />

          <InputField
            label="Short Description"
            placeholder="Brief overview (at least 50 characters)"
            value={draft.shortDescription || ''}
            onChangeText={(text) => onUpdate({ shortDescription: text })}
            errorText={errors.shortDescription}
            helperText={`${(draft.shortDescription || '').length}/50 characters minimum`}
            multiline
            numberOfLines={3}
            style={styles.field}
          />

          <InputField
            label="Long Description (Optional)"
            placeholder="Detailed description of the trip"
            value={draft.longDescription || ''}
            onChangeText={(text) => onUpdate({ longDescription: text })}
            multiline
            numberOfLines={5}
            style={styles.field}
          />

          <View style={styles.field}>
            <NumberStepper
              label="Duration (Days)"
              value={draft.duration || 7}
              onValueChange={(value) => onUpdate({ duration: value })}
              min={1}
              max={365}
              errorText={errors.duration}
            />
          </View>

          <View style={styles.field}>
            <SelectField
              label="Difficulty Level"
              value={draft.difficulty || 'moderate'}
              options={DIFFICULTY_OPTIONS}
              onValueChange={(value) => onUpdate({ difficulty: value as TemplateDifficulty })}
              errorText={errors.difficulty}
            />
          </View>

          <View style={styles.field}>
            <ThemedText style={styles.fieldLabel}>Travel Styles</ThemedText>
            <Caption color={theme.colors.textSecondary} style={styles.fieldHelper}>
              Select all that apply
            </Caption>
            <View style={styles.chipContainer}>
              {TRAVEL_STYLE_OPTIONS.map((style) => {
                const isSelected = (draft.travelStyle || []).includes(style.value);
                return (
                  <Pressable
                    key={style.value}
                    onPress={() => {
                      const current = draft.travelStyle || [];
                      const updated = isSelected
                        ? current.filter((s: string) => s !== style.value)
                        : [...current, style.value];
                      onUpdate({ travelStyle: updated });
                    }}
                    style={[
                      styles.chip,
                      {
                        backgroundColor: isSelected ? theme.colors.primary : theme.colors.surface,
                        borderColor: isSelected ? theme.colors.primary : theme.colors.borderMuted,
                      },
                    ]}
                  >
                    <Body style={{ color: isSelected ? theme.colors.surface : theme.colors.text }}>
                      {style.label}
                    </Body>
                  </Pressable>
                );
              })}
            </View>
            {errors.travelStyle && (
              <Caption color={theme.colors.error} style={styles.errorText}>
                {errors.travelStyle}
              </Caption>
            )}
          </View>

          <InputField
            label="Tags (optional)"
            placeholder="e.g. america-250, world-cup-2026, calgary-stampede"
            value={(draft.tags || []).join(', ')}
            onChangeText={(text) => {
              const tags = text
                .split(',')
                .map((segment) => normalizeTemplateTagInput(segment))
                .filter(Boolean);
              onUpdate({ tags });
            }}
            helperText="Comma-separated labels for search and filtering. Use consistent spelling for collections (e.g. calgary-stampede)."
            style={styles.field}
          />

          <InputField
            label="Highlights (optional)"
            placeholder={'e.g. Stadium tour access\nBest pre-match food streets\nEasy transit between match zones'}
            value={(draft.highlights || []).join('\n')}
            onChangeText={(text) => {
              const highlights = text
                .split(/\n|,/)
                .map((s) => s.trim())
                .filter(Boolean);
              onUpdate({ highlights });
            }}
            helperText="One per line. These appear as key selling points on the template detail page."
            multiline
            numberOfLines={4}
            style={styles.field}
          />

          <View style={styles.field}>
            <ThemedText style={styles.fieldLabel}>Best Months to Visit</ThemedText>
            <Caption color={theme.colors.textSecondary} style={styles.fieldHelper}>
              Select months when this trip is best experienced
            </Caption>
            <View style={styles.chipContainer}>
              {MONTH_OPTIONS.map((month) => {
                const isSelected = (draft.bestMonths || []).includes(month.value);
                return (
                  <Pressable
                    key={month.value}
                    onPress={() => {
                      const current = draft.bestMonths || [];
                      const updated = isSelected
                        ? current.filter((m: string) => m !== month.value)
                        : [...current, month.value];
                      onUpdate({ bestMonths: updated });
                    }}
                    style={[
                      styles.chip,
                      {
                        backgroundColor: isSelected ? theme.colors.primary : theme.colors.surface,
                        borderColor: isSelected ? theme.colors.primary : theme.colors.borderMuted,
                      },
                    ]}
                  >
                    <Body style={{ color: isSelected ? theme.colors.surface : theme.colors.text }}>
                      {month.label}
                    </Body>
                  </Pressable>
                );
              })}
            </View>
          </View>
        </View>
      </Card>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: 16,
  },
  section: {
    padding: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 4,
  },
  sectionDescription: {
    marginBottom: 16,
  },
  fields: {
    gap: 16,
  },
  field: {
    marginBottom: 8,
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
  },
  fieldHelper: {
    marginBottom: 8,
  },
  chipContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  chip: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 16,
    borderWidth: 1,
  },
  errorText: {
    marginTop: 4,
  },
});

